/**
 * RetiredSessionPanel — what a retired session's page shows (migration 758).
 *
 * A retired session is hidden from every list but kept whole. Opening it
 * directly shows who retired it, when and why, and offers Take back through
 * the shared ReverseActionButton (restore_retired_session: reason required;
 * the retirer, the facilitator, the creator or a workspace admin — the server
 * decides and any refusal is shown in the dialog).
 */
import { Link } from 'react-router-dom';
import { Callout } from '@/components/ds';
import { ReverseActionButton } from './ReverseActionButton';
import { useSessionStore } from '@/stores/prioritization/sessionStore';
import type { PrioritizationSession } from '@/types/prioritization';

function formatWhen(value: PrioritizationSession['retiredAt']): string | null {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleString();
}

export function RetiredSessionPanel({ session }: { session: PrioritizationSession }) {
  const restoreRetiredSession = useSessionStore((s) => s.restoreRetiredSession);
  const when = formatWhen(session.retiredAt);

  return (
    <div className="mx-auto max-w-2xl space-y-4 py-8">
      <Link to="/prioritization/sessions" className="text-sm text-[var(--text-ter)] hover:text-[var(--text-sec)]">
        ← Sessions
      </Link>
      <h1 className="text-2xl font-semibold text-[var(--text-pri)]">{session.name || 'Untitled session'}</h1>
      <Callout
        tone="mustard"
        label="Retired"
        title="This session was retired"
        action={
          <ReverseActionButton
            label="Take back"
            confirmTitle="Take back this session"
            explain="The session comes back to your lists in the state it was retired in, with everything inside it."
            run={(reason) => restoreRetiredSession(session.id, reason)}
          />
        }
      >
        <p className="text-sm text-[var(--text-sec)]">
          It is hidden from every list. Nothing inside it was deleted.
          {when ? ` Retired ${when}.` : ''}
        </p>
        {session.retiredReason && (
          <p className="mt-1 text-sm text-[var(--text-sec)]">Reason: {session.retiredReason}</p>
        )}
      </Callout>
    </div>
  );
}
